// client/src/pages/KnowledgeArticle.js
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ARTICLES, KNOWLEDGE_CATEGORIES } from '../data/knowledgeData';

export default function KnowledgeArticle() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.subscription?.status === 'active';

  const article = ARTICLES.find(a => a.slug === slug);
  const [activeSection, setActiveSection] = useState(0);

  if (!article) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 48 }}>📄</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 400, color: 'var(--ink)' }}>Article not found</div>
        <button onClick={() => navigate('/knowledge')} style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '10px 24px', borderRadius: 0, cursor: 'pointer' }}>← Back to Knowledge</button>
      </div>
    );
  }

  const locked = article.pro && !isPro;
  const cat = KNOWLEDGE_CATEGORIES.find(c => c.id === article.category);
  const LEVEL_COLOR = { 'Beginner': '#16a34a', 'Intermediate': '#d97706', 'Advanced': '#dc2626' };

  // Related articles (same category, not same slug)
  const related = ARTICLES.filter(a => a.category === article.category && a.slug !== slug).slice(0, 3);

  if (locked) {
    return (
      <div style={{ minHeight: 'calc(100vh - 56px)', background: 'var(--paper)' }}>
        {/* Header */}
        <div style={{ background: 'var(--paper)', color: 'var(--ink)', padding: '48px 24px 40px', borderBottom: '2px solid var(--ink)' }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <button onClick={() => navigate('/knowledge')} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 20, padding: 0 }}>← KNOWLEDGE CENTER</button>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <span style={{ background: 'var(--paper-2)', color: 'var(--muted-2)', fontSize: 10, padding: '2px 10px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{cat?.title}</span>
              <span style={{ background: LEVEL_COLOR[article.level] + '22', color: LEVEL_COLOR[article.level], fontSize: 10, padding: '2px 10px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{article.level}</span>
              <span style={{ background: 'var(--ink)', color: 'var(--paper)', fontSize: 10, padding: '2px 10px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>🔒 PRO</span>
            </div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 4vw, 38px)', lineHeight: 1.1, marginBottom: 12, color: 'var(--ink)' }}>{article.title}</h1>
            <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>{article.subtitle}</p>
          </div>
        </div>

        {/* Locked State */}
        <div style={{ maxWidth: 900, margin: '48px auto', padding: '0 24px' }}>
          {/* Preview of article */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted-2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 12 }}>Preview</div>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.75, marginBottom: 0 }}>{article.summary}</p>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted-2)', marginTop: 16 }}>
              {article.sections.length} sections · {article.readTime} min read · {article.tags.map(t => `#${t}`).join(' ')}
            </div>
            {/* Blur overlay */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80, background: 'linear-gradient(transparent, var(--card))' }} />
          </div>

          {/* Sections preview (locked) */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '24px 28px', marginBottom: 24 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted-2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 16 }}>Article Contents</div>
            {article.sections.map((sec, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < article.sections.length - 1 ? '1px solid var(--line-card)' : 'none' }}>
                <div style={{ width: 24, height: 24, background: 'var(--paper-2)', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--muted-2)', flexShrink: 0 }}>{i + 1}</div>
                <div style={{ fontSize: 14, color: 'var(--muted)', flex: 1 }}>{sec.title}</div>
                <span style={{ fontSize: 16, color: 'var(--muted-2)' }}>🔒</span>
              </div>
            ))}
          </div>

          {/* Upgrade CTA */}
          <div style={{ background: 'var(--ink)', borderRadius: 0, padding: '40px 36px', textAlign: 'center', color: 'var(--paper)' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔬</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 8 }}>Unlock This Article</div>
            <p style={{ color: 'var(--muted-2)', fontSize: 14, maxWidth: 460, margin: '0 auto 24px' }}>
              Get full access to all {ARTICLES.filter(a => a.pro).length} Pro articles, {' '}
              advanced statistical tools, AI protocol builder, IRB checklists, and more.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 28 }}>
              {['Advanced articles & tools', 'AI Protocol Builder', 'CONSORT Checklist', 'Advanced Stats Calculator', 'Risk Assessment Matrix'].map(f => (
                <span key={f} style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--muted-2)', fontSize: 11, padding: '5px 12px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>✓ {f}</span>
              ))}
            </div>
            <button onClick={() => navigate('/profile')} style={{ background: 'var(--paper)', color: 'var(--ink)', border: 'none', padding: '13px 36px', borderRadius: 0, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginRight: 12 }}>
              Upgrade to Pro →
            </button>
            <button onClick={() => navigate('/knowledge')} style={{ background: 'transparent', color: 'var(--paper)', border: '1px solid rgba(255,255,255,0.3)', padding: '13px 24px', borderRadius: 0, fontSize: 14, cursor: 'pointer' }}>
              Browse Free Articles
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: 'calc(100vh - 56px)', background: 'var(--paper)' }}>
      {/* Article Header */}
      <div style={{ background: 'var(--paper)', color: 'var(--ink)', padding: '48px 24px 40px', borderBottom: '2px solid var(--ink)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <button onClick={() => navigate('/knowledge')} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 20, padding: 0 }}>← KNOWLEDGE CENTER</button>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ background: 'var(--paper-2)', color: 'var(--muted-2)', fontSize: 10, padding: '3px 10px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{cat?.icon} {cat?.title}</span>
            <span style={{ background: LEVEL_COLOR[article.level] + '22', color: LEVEL_COLOR[article.level], fontSize: 10, padding: '3px 10px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{article.level}</span>
            <span style={{ color: 'var(--muted-2)', fontSize: 11, fontFamily: 'var(--font-mono)', padding: '3px 0' }}>{article.readTime} min read</span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 4vw, 42px)', lineHeight: 1.08, marginBottom: 12, color: 'var(--ink)' }}>{article.title}</h1>
          <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.6, maxWidth: 680 }}>{article.subtitle}</p>
          <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
            {article.tags.map(tag => (
              <span key={tag} style={{ background: 'var(--paper-2)', color: 'var(--muted-2)', fontSize: 10, padding: '3px 9px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>#{tag}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="knowledge-layout" style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: '1fr 280px', gap: 28 }}>
        {/* Main Content */}
        <div>
          {/* Summary box */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderLeft: '4px solid var(--ink)', borderRadius: 0, padding: '20px 24px', marginBottom: 28 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 8 }}>Article Summary</div>
            <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.75, margin: 0 }}>{article.summary}</p>
          </div>

          {/* Sections */}
          {article.sections.map((section, idx) => (
            <div key={idx} style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, marginBottom: 4, overflow: 'hidden' }}>
              <div
                onClick={() => setActiveSection(activeSection === idx ? -1 : idx)}
                style={{ padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: activeSection === idx ? 'var(--ink)' : 'var(--card)', transition: 'background 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: activeSection === idx ? 'var(--muted-2)' : 'var(--muted-2)', minWidth: 24 }}>{String(idx + 1).padStart(2, '0')}</span>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 400, color: activeSection === idx ? 'var(--paper)' : 'var(--ink)' }}>{section.title}</span>
                </div>
                <span style={{ fontSize: 12, color: activeSection === idx ? 'var(--muted-2)' : 'var(--muted-2)' }}>{activeSection === idx ? '▲' : '▼'}</span>
              </div>
              {activeSection === idx && (
                <div style={{ padding: '24px 28px', borderTop: '1px solid var(--line-card)' }}>
                  <ArticleContent content={section.content} />
                </div>
              )}
            </div>
          ))}

          {/* Open all */}
          <button
            onClick={() => setActiveSection(activeSection === -2 ? -1 : -2)}
            style={{ background: 'none', border: '1px solid var(--line)', padding: '9px 18px', borderRadius: 0, cursor: 'pointer', fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase', marginTop: 8 }}>
            {activeSection === -2 ? 'Collapse All' : 'Expand All Sections'}
          </button>
        </div>

        {/* Sidebar */}
        <div>
          {/* Table of Contents */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '20px', marginBottom: 16, position: 'sticky', top: 16 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 14 }}>Contents</div>
            {article.sections.map((sec, i) => (
              <div key={i} onClick={() => setActiveSection(i)} style={{ display: 'flex', gap: 8, padding: '7px 0', cursor: 'pointer', borderBottom: i < article.sections.length - 1 ? '1px solid var(--line-card)' : 'none' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted-2)', minWidth: 20 }}>{i + 1}.</span>
                <span style={{ fontSize: 12, color: activeSection === i ? 'var(--ink)' : 'var(--muted)', fontWeight: activeSection === i ? 700 : 400, lineHeight: 1.4 }}>{sec.title}</span>
              </div>
            ))}
          </div>

          {/* Related Articles */}
          {related.length > 0 && (
            <div style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '20px' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '1.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 14 }}>Related Articles</div>
              {related.map(rel => {
                const relLocked = rel.pro && !isPro;
                return (
                  <div key={rel.slug} onClick={() => navigate(`/knowledge/${rel.slug}`)}
                    style={{ padding: '10px 0', borderBottom: '1px solid var(--line-card)', cursor: 'pointer' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 400, color: 'var(--ink)', lineHeight: 1.35, marginBottom: 3 }}>{rel.title}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span style={{ fontSize: 10, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>{rel.readTime}m</span>
                      {relLocked && <span style={{ fontSize: 10, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>🔒 PRO</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pro CTA */}
          {!isPro && (
            <div style={{ background: 'var(--ink)', borderRadius: 0, padding: '20px', marginTop: 16, color: 'var(--paper)', textAlign: 'center' }}>
              <div style={{ fontSize: 20, marginBottom: 8 }}>⭐</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 400, marginBottom: 6 }}>Unlock Pro Knowledge</div>
              <p style={{ fontSize: 12, color: 'var(--muted-2)', lineHeight: 1.5, marginBottom: 14 }}>Advanced articles, AI tools, calculators & more</p>
              <button onClick={() => navigate('/profile')} style={{ background: 'var(--paper)', color: 'var(--ink)', border: 'none', padding: '9px 18px', borderRadius: 0, fontSize: 12, fontWeight: 700, cursor: 'pointer', width: '100%' }}>Upgrade →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Renders markdown-like content: **bold**, headings, bullets
function ArticleContent({ content }) {
  const lines = content.split('\n');
  return (
    <div style={{ fontSize: 14, lineHeight: 1.85, color: 'var(--ink-2)' }}>
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} style={{ height: 10 }} />;
        if (line.startsWith('**') && line.endsWith('**') && !line.slice(2, -2).includes('**')) {
          return <div key={i} style={{ fontWeight: 700, color: 'var(--ink)', marginTop: 18, marginBottom: 4, fontSize: 15 }}>{line.slice(2, -2)}</div>;
        }
        if (line.startsWith('- ')) {
          return <div key={i} style={{ paddingLeft: 16, position: 'relative', marginBottom: 4 }}>
            <span style={{ position: 'absolute', left: 0, color: 'var(--ink)', fontWeight: 700 }}>·</span>
            <RenderInline text={line.slice(2)} />
          </div>;
        }
        if (/^\d+\.\s/.test(line)) {
          const num = line.match(/^(\d+)\./)[1];
          return <div key={i} style={{ paddingLeft: 28, position: 'relative', marginBottom: 5 }}>
            <span style={{ position: 'absolute', left: 0, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted-2)', top: 3 }}>{num}.</span>
            <RenderInline text={line.replace(/^\d+\.\s/, '')} />
          </div>;
        }
        return <p key={i} style={{ margin: '0 0 10px' }}><RenderInline text={line} /></p>;
      })}
    </div>
  );
}

function RenderInline({ text }) {
  // Handle **bold** inline
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={i} style={{ color: 'var(--ink)' }}>{part.slice(2, -2)}</strong>
          : <span key={i}>{part}</span>
      )}
    </>
  );
}